// Which bill an entry is a due date of: its biller (see server/billers.js) when it
// has one, otherwise the same payee + type.
export const billKey = (bill) => (bill.biller_id ? `biller:${bill.biller_id}` : `${bill.payee}::${bill.type}`);

// Groups raw bill rows (which include historical Paid occurrences alongside the
// current/upcoming one) into a single representative row per recurring bill
// (see billKey). Prefers the earliest unpaid occurrence, so an
// overdue EMI isn't hidden behind next month's; falls back to the most recent
// one if every occurrence has been settled.
export const groupCurrentBills = (bills) => {
  const groups = new Map();
  for (const bill of bills) {
    const key = billKey(bill);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(bill);
  }

  return Array.from(groups.values()).map((group) => {
    const sorted = [...group].sort((a, b) => (a.dueDate < b.dueDate ? 1 : a.dueDate > b.dueDate ? -1 : 0));
    const unpaid = sorted.filter((b) => b.status === "Pending");
    return unpaid.length ? unpaid[unpaid.length - 1] : sorted[0];
  });
};

// Spending splits into fixed (repeats: EMIs, SIPs, rent, subscriptions - anything
// with a frequency, a payment of a bill on Bills & Payments, made from a loan or
// investment, of a recurring type, or the same payee + amount showing up in more
// than one month) and random (one-time).
const FIXED_TYPES = new Set(["Loans", "Investments", "Subscription", "Rent", "Utilities", "Insurance"]);

export const isFixedExpense = (bill) =>
  !!bill.sourceKind || !!bill.biller_id || FIXED_TYPES.has(bill.type) || (!!bill.repeatUnit && bill.repeatUnit !== "once");

const repeatKey = (bill) => `${String(bill.payee || "").trim().toLowerCase()}::${Math.round(bill.amount)}`;

// A fixed-expense test that also catches repeats entered one at a time: the same
// payee and amount in two or more different months. Pass every bill (not just one
// month's) so repeats across months are seen.
export const fixedExpenseTest = (bills) => {
  const months = new Map();
  for (const b of bills) {
    const key = repeatKey(b);
    if (!months.has(key)) months.set(key, new Set());
    months.get(key).add(String(b.dueDate).slice(0, 7));
  }
  return (bill) => isFixedExpense(bill) || (months.get(repeatKey(bill))?.size ?? 0) >= 2;
};

// Unpaid entries of the same bill (payee + type) folded into one row, so five missed
// SIP instalments show as "5 unpaid" instead of five rows. Groups are ordered by
// their earliest due date; each keeps its entries oldest first and their total.
export const groupUnpaid = (bills) => {
  const groups = new Map();
  for (const bill of [...bills].sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0))) {
    const key = billKey(bill);
    if (!groups.has(key)) groups.set(key, { key, payee: bill.payee, type: bill.type, items: [], total: 0 });
    const g = groups.get(key);
    g.items.push(bill);
    g.total += bill.amount;
  }
  return Array.from(groups.values());
};

// The name of the bank account or card an entry is paid from, or "" when not tracked.
// `data` has the `assets` and `cards` lists.
export const paidFromName = (bill, data) => {
  if (!bill.paidFromKind) return "";
  const list = bill.paidFromKind === "card" ? data.cards || [] : data.assets || [];
  const found = list.find((x) => x.id === bill.paidFromId);
  return found ? found.name : bill.paidFromKind === "card" ? "A deleted card" : "A deleted account";
};

export const daysUntilDue =(dueDate, referenceDate = new Date()) => {
  const due = new Date(`${dueDate}T00:00:00`);
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / (1000 * 60 * 60 * 24));
};

// How an unpaid bill reads, by its date:
//   date passed      -> "Due"      (needs paying now)
//   today to 7 days  -> "Pending"  (with the days left shown next to it)
//   further out      -> "Upcoming"
// Paid bills are always "Paid". This only affects how status is DISPLAYED - the
// stored status stays Paid/Pending.
export const getBillDisplayStatus = (bill) => {
  if (bill.status === "Paid") return "Paid";
  const days = daysUntilDue(bill.dueDate);
  if (days < 0) return "Due";
  return days <= 7 ? "Pending" : "Upcoming";
};
